/**
 * TODO: infinite scroll logic
 * TODO!: preserve searchBar text after navigating to a searched plot
 */

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Routes } from "../../routes";
import "./home.css";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { SectionCard } from "react-library";
import { selectedPlotState } from "../../state/atoms/global.atom";
import {
  daysBtwTwoDates,
  roundDecimalNumber,
  truncateWithEllipsis,
} from "../../utils";
import { getFarmData, TFarmDataResponse } from "../../services/farm.service";
import { fetchUserPlots } from "../../services/user.service";
import { TSectionCardsData } from "./types";
import { FaBell } from "react-icons/fa";
import { IoSearchSharp } from "react-icons/io5";
import dayjs from "dayjs";
import { t } from "../../services/locale.service";

export const TestIds = {
  onboardingLoader: "onboarding-loading",
  unauthorizedScreen: "unauthorized-testid",
  noPlotScreen: "noplot-testid",
  searchedPlotNotFound: "searched-plot-notfound",
  searchBar: "plot-search-bar",
  plotsContainer: "plot-container",
  searchedPlotsContainer: "searched-plots-container",
};

export const UNAUTHORIZED_ERROR = "UnauthorizedError";

const sectionCardsData: TSectionCardsData = {
  cropStage: {
    id: "cropStage",
    cardName: "crop",
    cropName: "",
    stageName: "",
    emojiIcon: "🌱",
    cardOverviewData: [
      {
        key: "stageDays",
        value: "144",
      },
      {
        key: "seasonDays",
        value: "177",
      },
    ],
  },
  sensorData: {
    id: "sensorData",
    cardName: "sensorData",
    emojiIcon: "🌡️",
    cardOverviewData: [
      {
        key: "Air Humidity",
        value: "75.65 %",
      },
      {
        key: "Air Temperature",
        value: "24.6 C",
      },
    ],
  },
  diseaseSensorData: {
    id: "diseaseSensorData",
    cardName: "Disease",
    emojiIcon: "🦠",
    cardOverviewData: [],
  },
  pestSensorData: {
    id: "pestSensorData",
    cardName: "Pest",
    emojiIcon: "🐛",
    cardOverviewData: [],
  },
  fertigation: {
    id: "fertigation",
    emojiIcon: "🚜",
    cardName: "fertigationType",
    cardOverviewData: [],
  },
  sprayData: {
    id: "sprayData",
    emojiIcon: "🚿",
    cardName: "Spray",
    cardOverviewData: [],
  },
  calendar: {
    id: "calendar",
    emojiIcon: "🗓️",
    cardName: "calendar",
    cardOverviewData: [],
  },
  others: {
    id: "others",
    emojiIcon: "⚙️",
    cardName: "others",
    cardOverviewData: [],
  },
  weather: {
    id: "weather",
    emojiIcon: "☁",
    cardName: "weather_prediction",
    cardOverviewData: [],
  },
};

const OrgHome: React.FC = () => {
  const { orgId } = useParams();

  const selectedPlot = useRecoilValue(selectedPlotState);
  const setSelectedPlot = useSetRecoilState(selectedPlotState);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSectionCardsLoading, setIsSectionCardsLoading] =
    useState<boolean>(true);

  const [sectionCards, setSectionCards] = useState(sectionCardsData);
  const [addCrop, setAddCrop] = useState<boolean>(false);
  const [timeStamp, setTimeStamp] = useState<string>();

  const navigate = useNavigate();
  const handleSectionClick = (sectionId: string | undefined) => {
    try {
      if (selectedPlot) {
        switch (sectionId) {
        case "cropStage":
          if (orgId) {
            navigate(
              Routes.orgCropStageDetails
                .replace(":plotId", selectedPlot.plotId)
                .replace(":orgId", orgId)
            );
          } else
            navigate(
              Routes.cropStageDetails.replace(":plotId", selectedPlot.plotId)
            );
          break;

        case "sensorData":
          if (orgId) {
            navigate(
              Routes.sensors
                .replace(":plotId", selectedPlot.plotId)
                .replace(":orgId", orgId)
            );
          }
          break;

        case "sprayData":
          if (orgId) {
            navigate(
              Routes.orgSpray
                .replace(":plotId", selectedPlot.plotId)
                .replace(":orgId", orgId)
            );
          }
          break;

        case "diseaseSensorData":
          if (orgId) {
            navigate(
              Routes.diseaseSensors
                .replace(":plotId", selectedPlot.plotId)
                .replace(":orgId", orgId)
            );
          }
          break;

        case "pestSensorData":
          if (orgId) {
            navigate(
              Routes.pestSensors
                .replace(":plotId", selectedPlot.plotId)
                .replace(":orgId", orgId)
            );
          }
          break;

        case "fertigation":
          if (orgId) {
            navigate(
              Routes.orgFertigation
                .replace(":plotId", selectedPlot.plotId)
                .replace(":orgId", orgId)
            );
          }
          break;

        case "calendar":
          if (orgId) {
            navigate(
              Routes.calendar
                .replace(":plotId", selectedPlot.plotId)
                .replace(":orgId", orgId)
            );
          }
          break;

        case "others":
          if (orgId) {
            navigate(
              Routes.others
                .replace(":plotId", selectedPlot.plotId)
                .replace(":orgId", orgId)
            );
          }
          break;
        case "weather":
          if (orgId) {
            navigate(Routes.weather.replace(":plotId", selectedPlot.plotId));
          }
          break;
        default:
          navigate(Routes.home);
          break;
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchPlotData = async (plotId: string) => {
    try {
      setIsSectionCardsLoading(true);
      const farmData = await getFarmData(plotId);
      setTimeStamp(
        dayjs(farmData?.timestamp?.description).format("DD MMM YYYY hh:mm A")
      );
      Object.entries(sectionCards).map(([key]) =>
        constructCardOverviewData(farmData, key)
      );
    } catch (error) {
      console.error("error farmdata", error);
    } finally {
      setIsSectionCardsLoading(false);
    }
  };

  const fetchPlots = async () => {
    try {
      setIsLoading(true);
      const plots = await fetchUserPlots({}, { skip: 0, limit: 20 });
      if (plots?.length) {
        setSelectedPlot(plots[0]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlotBarClick = () => {
    try {
      navigate(Routes.searchPlots.replace(":orgId", orgId!));
    } catch (error) {
      console.log("error");
    }
  };
  const handleNotificationClick = () => {
    try {
      if (selectedPlot?.plotId && orgId) {
        navigate(
          Routes.notifications
            .replace(":plotId", selectedPlot.plotId)
            .replace(":orgId", orgId)
        );
      } else {
        console.error("Plot or Organization ID is missing");
      }
    } catch (error) {
      console.error("Error navigating to notifications:", error);
    }
  };

  const constructCardOverviewData = (
    farmData: TFarmDataResponse,
    sectionId: string
  ) => {
    try {
      switch (sectionId) {
      case "cropStage": {
        const updatedCardOverviewData =
            sectionCards[sectionId].cardOverviewData;
        const selectedFarmDetailKey = "cropStageData";
        
        if (
          !farmData?.[selectedFarmDetailKey] ||
            !!farmData?.[selectedFarmDetailKey]?.isCropEnded
        ) {
          setAddCrop(true);
          break;
        }
        const stageStartDateArr =
            farmData?.[selectedFarmDetailKey]?.stageStartDate;
        if (stageStartDateArr?.length && updatedCardOverviewData) {
          const activeStageIndx = stageStartDateArr?.length - 1;
          const daysSinceLastActiveStage = daysBtwTwoDates(
            new Date(),
            new Date(stageStartDateArr[activeStageIndx])
          );
          updatedCardOverviewData[0].value = `${daysSinceLastActiveStage.toString()}`;

          const daysSinceStartOfSeason = daysBtwTwoDates(
            new Date(),
            new Date(stageStartDateArr[0])
          );
          updatedCardOverviewData[1].value = `${daysSinceStartOfSeason.toString()}`;
          
          // Remove any existing crop information to prevent accumulation
          const cropKeys = ["crop", "stage", "cropVariety"];
          const filteredCardOverviewData = updatedCardOverviewData.filter(
            item => !cropKeys.includes(item.key)
          );
          
          // Add crop information as additional items in cardOverviewData
          if (farmData?.[selectedFarmDetailKey]) {
            const cropData = farmData[selectedFarmDetailKey];
            
            // Add crop name
            if (cropData.name) {
              filteredCardOverviewData.push({
                key: "crop",
                value: cropData.name,
              });
            }
            
            // Add stage name
            if (cropData.activeStage) {
              filteredCardOverviewData.push({
                key: "stage", 
                value: cropData.activeStage,
              });
            }
            
            // Add variety
            if (cropData.variety) {
              filteredCardOverviewData.push({
                key: "cropVariety",
                value: cropData.variety,
              });
            }
          }
          
          // Use the filtered array
          updatedCardOverviewData.length = 0;
          updatedCardOverviewData.push(...filteredCardOverviewData);
          
          const updatedSectionCards = {
            ...sectionCards,
            [sectionId]: {
              ...sectionCards[sectionId],
              cardOverviewData: updatedCardOverviewData,
            },
          };
          
          // Debug: Log the updated section cards to see what crop info is being set
          console.log("Updated section cards for crop stage:", updatedSectionCards[sectionId]);
          
          setSectionCards(updatedSectionCards);
        }
        break;
      }
      case "sensorData": {
        let updatedCardOverviewData = [
          {
            key: "soilMoisture1",
            value: farmData["soilMoisture1"]
              ? `${roundDecimalNumber(
                Number(farmData["soilMoisture1"].value),
                2
              ).toString()} kPa`
              : "UNAVAILABLE",
          },
          {
            key: "soilMoisture2",
            value: farmData["soilMoisture2"]
              ? `${roundDecimalNumber(
                Number(farmData["soilMoisture2"].value),
                2
              ).toString()} kPa`
              : "UNAVAILABLE",
          },
          {
            key: "moisture1",
            value: farmData["moisture1"]
              ? `${roundDecimalNumber(
                Number(farmData["moisture1"].value),
                2
              ).toString()} %`
              : "UNAVAILABLE",
          },
          {
            key: "moisture2",
            value: farmData["moisture2"]
              ? `${roundDecimalNumber(
                Number(farmData["moisture2"].value),
                2
              ).toString()} %`
              : "UNAVAILABLE",
          },
          {
            key: "soilTemp",
            value: farmData["soilTemp"]
              ? `${roundDecimalNumber(
                Number(farmData["soilTemp"].value),
                2
              ).toString()} °C`
              : "UNAVAILABLE",
          },
          {
            key: "airTemp",
            value: farmData["airTemp"]
              ? `${roundDecimalNumber(
                Number(farmData["airTemp"].value),
                2
              ).toString()} °C`
              : "UNAVAILABLE",
          },
          {
            key: "airHumidity",
            value: farmData["airHumidity"]
              ? `${roundDecimalNumber(
                Number(farmData["airHumidity"].value),
                2
              ).toString()} %`
              : "UNAVAILABLE",
          },
          {
            key: "rainFall",
            value: farmData["rainFall"]
              ? `${roundDecimalNumber(
                Number(farmData["rainFall"].value),
                2
              ).toString()} mm`
              : "UNAVAILABLE",
          },
          {
            key: "windSpeed",
            value: farmData["windSpeed"]
              ? `${roundDecimalNumber(
                Number(farmData["windSpeed"].value),
                2
              ).toString()} kmph`
              : "UNAVAILABLE",
          },
          {
            key: "lightIntensity",
            value: farmData["lightIntensity"]
              ? `${roundDecimalNumber(
                Number(farmData["lightIntensity"].value),
                2
              ).toString()} lux`
              : "UNAVAILABLE",
          },
          {
            key: "leafWetness",
            value: farmData["leafWetness"]
              ? `${roundDecimalNumber(
                Number(farmData["leafWetness"].value),
                2
              ).toString()}`
              : "UNAVAILABLE",
          },
        ];
        updatedCardOverviewData = updatedCardOverviewData.filter(
          (each) => each.value != "UNAVAILABLE"
        );
        const updatedSectionCards = {
          ...sectionCards,
          sensorData: {
            ...sectionCards[sectionId],
            cardOverviewData: updatedCardOverviewData,
          },
        };
        setSectionCards(() => updatedSectionCards);
        break;
      }

      default:
        break;
      }
      return [];
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (!selectedPlot) {
      fetchPlots();
    }
  }, []);

  useEffect(() => {
    if (selectedPlot) {
      fetchPlotData(selectedPlot.plotId);
    }
  }, [selectedPlot]);

  return (
    <div className="container">
      {/* <ScreenHeader screenName={"Your Plot"} disableBackButton={true}/> */}
      <div className="appContainer">
        <div className="plotBarContainer">
          <div className="plot-bar-button" onClick={handlePlotBarClick}>
            <div
              className={selectedPlot ? "search-value" : "search-placeholder"}
            >
              {selectedPlot
                ? truncateWithEllipsis(selectedPlot.name, 20)
                : "Select a Plot"}
            </div>

            <div className="btn-container">
              {selectedPlot && (
                <div className="home-searchicon-container">
                  <IoSearchSharp size={18} />
                </div>
              )}

              {selectedPlot && (
                <div
                  className="notification-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNotificationClick();
                  }}
                >
                  <FaBell className="notification-icon" />
                </div>
              )}
            </div>
          </div>
        </div>

        {isSectionCardsLoading || isLoading ? (
          <div style={{ textAlign: "center", padding: 10 }}>{t("Loading")}</div>
        ) : !selectedPlot ? (
          <div style={{ overflowX: "hidden", padding: 10 }}>
            {t("Please_Select_a_Plot")}
          </div>
        ) : (
          <div className="sectionContainer">
            <div className="row">
              {!addCrop ? (
                <div
                  className="midSectionCard"
                  onClick={() => handleSectionClick(sectionCards.cropStage.id)}
                >
                  <SectionCard
                    iconEmoji={sectionCards.cropStage.emojiIcon}
                    cardName={sectionCards.cropStage.cardName}
                    cardOverviewData={sectionCards.cropStage.cardOverviewData}
                  />
                </div>
              ) : (
                <div
                  className="midSectionCard"
                  onClick={() =>
                    navigate(
                      Routes.addCrop.replace(":plotId", selectedPlot.plotId)
                    )
                  }
                >
                  <div className="card">
                    <h2 className="card-name">{t("Add_Crop")}</h2>
                  </div>
                </div>
              )}
              <div className="multipleSectionCardWrapper">
                <div
                  className="smallSectionCard"
                  onClick={() =>
                    handleSectionClick(sectionCards.diseaseSensorData.id)
                  }
                >
                  <SectionCard
                    iconEmoji={sectionCards.diseaseSensorData.emojiIcon}
                    cardName={sectionCards.diseaseSensorData.cardName}
                    cardOverviewData={
                      sectionCards.diseaseSensorData.cardOverviewData
                    }
                  />
                </div>
                <div
                  className="smallSectionCard"
                  onClick={() =>
                    handleSectionClick(sectionCards.pestSensorData.id)
                  }
                >
                  <SectionCard
                    iconEmoji={sectionCards.pestSensorData.emojiIcon}
                    cardName={sectionCards.pestSensorData.cardName}
                    cardOverviewData={
                      sectionCards.pestSensorData.cardOverviewData
                    }
                  />
                </div>
              </div>
            </div>

            <div className="row">
              <div
                className="wideSectionCard"
                onClick={() => handleSectionClick(sectionCards.sensorData.id)}
              >
                <SectionCard
                  iconEmoji={sectionCards.sensorData.emojiIcon}
                  cardName={sectionCards.sensorData.cardName}
                  cardOverviewData={sectionCards.sensorData.cardOverviewData}
                  timeStamp={timeStamp}
                />
              </div>
            </div>

            <div className="row">
              <div
                className="midSectionCard"
                onClick={() => handleSectionClick(sectionCards.sprayData.id)}
              >
                <SectionCard
                  iconEmoji={sectionCards.sprayData.emojiIcon}
                  cardName={sectionCards.sprayData.cardName}
                  cardOverviewData={sectionCards.sprayData.cardOverviewData}
                />
              </div>
              <div
                className="midSectionCard"
                onClick={() => handleSectionClick(sectionCards.fertigation.id)}
              >
                <SectionCard
                  iconEmoji={sectionCards.fertigation.emojiIcon}
                  cardName={sectionCards.fertigation.cardName}
                  cardOverviewData={sectionCards.fertigation.cardOverviewData}
                />
              </div>
            </div>

            <div className="row">
              <div
                className="wideSectionCard"
                onClick={() => handleSectionClick(sectionCards.weather.id)}
              >
                <SectionCard
                  iconEmoji={sectionCards.weather.emojiIcon}
                  cardName={sectionCards.weather.cardName}
                  cardOverviewData={sectionCards.weather.cardOverviewData}
                />
              </div>
            </div>

            <div className="row">
              <div
                className="midSectionCard"
                onClick={() => handleSectionClick(sectionCards.calendar.id)}
              >
                <SectionCard
                  iconEmoji={sectionCards.calendar.emojiIcon}
                  cardName={sectionCards.calendar.cardName}
                  cardOverviewData={sectionCards.calendar.cardOverviewData}
                />
              </div>
              <div
                className="midSectionCard"
                onClick={() => handleSectionClick(sectionCards.others.id)}
              >
                <SectionCard
                  iconEmoji={sectionCards.others.emojiIcon}
                  cardName={sectionCards.others.cardName}
                  cardOverviewData={sectionCards.others.cardOverviewData}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgHome;
